/**
 * Line-item table column catalog for the document template editor.
 *
 * Column headings live in `template.itemsTable`, not as hardcoded `<th>` text.
 * Templates render them via `{{itemsTable.columns.description.label}}`, merged
 * at compile time the same way the backend does for preview and PDF.
 */

const ALIGNMENTS = ["left", "right"];
export const MAX_HEADING_LENGTH = 40;

const column = (key, defaultLabel, align, bindHint, cssClass) => ({
  key,
  defaultLabel,
  label: defaultLabel,
  align,
  bindHint,
  cssClass,
  visible: true,
});

/** Fallback when GET /items-table-catalog is unavailable. */
export const FALLBACK_ITEMS_TABLE_CATALOG = Object.freeze({
  invoice: Object.freeze([
    column("description", "DESCRIPTION", "left", "{{name}}", "col-desc"),
    column("units", "UNITS", "right", "{{quantity}}", "col-units"),
    column(
      "unitPrice",
      "UNIT PRICE",
      "right",
      "{{money ../invoice.currency price}}",
      "col-price"
    ),
    column(
      "discount",
      "DISCOUNT",
      "right",
      '{{money ../invoice.currency discount wrap="parens"}}',
      "col-disc"
    ),
    column("currency", "CURRENCY", "right", "{{../invoice.currency}}", "col-curr"),
    column(
      "amount",
      "AMOUNT",
      "right",
      "{{money ../invoice.currency amount}}",
      "col-amt"
    ),
  ]),
  proposal: Object.freeze([
    column("description", "DESCRIPTION", "left", "{{name}}", "col-desc"),
    column("units", "UNITS", "right", "{{quantity}}", "col-units"),
    column(
      "unitPrice",
      "UNIT PRICE",
      "right",
      "{{money ../proposal.currency price}}",
      "col-price"
    ),
    column(
      "discount",
      "DISCOUNT",
      "right",
      '{{money ../proposal.currency discount wrap="parens"}}',
      "col-disc"
    ),
    column(
      "amount",
      "AMOUNT",
      "right",
      "{{money ../proposal.currency amount}}",
      "col-amt"
    ),
  ]),
  upsell: Object.freeze([
    column("description", "DESCRIPTION", "left", "{{name}}", "col-desc"),
    column("units", "UNITS", "right", "{{quantity}}", "col-units"),
    column(
      "unitPrice",
      "UNIT PRICE",
      "right",
      "{{money ../upsell.currency price}}",
      "col-price"
    ),
    column(
      "discount",
      "DISCOUNT",
      "right",
      '{{money ../upsell.currency discount wrap="parens"}}',
      "col-disc"
    ),
    column(
      "amount",
      "AMOUNT",
      "right",
      "{{money ../upsell.currency amount}}",
      "col-amt"
    ),
  ]),
});

export const templateDocumentType = (type) => {
  if (type === "proposal") return "proposal";
  if (type === "upsell") return "upsell";
  return "invoice";
};

export const catalogForType = (type) =>
  FALLBACK_ITEMS_TABLE_CATALOG[templateDocumentType(type)] || [];

export const catalogKeys = (type) => catalogForType(type).map((col) => col.key);

export const isCatalogKey = (type, key) =>
  catalogKeys(type).includes(String(key ?? "").trim());

/** Friendly name for the panel: "unitPrice" → "Unit price". */
export const friendlyColumnName = (key) => {
  const labels = {
    description: "Description",
    units: "Units",
    unitPrice: "Unit price",
    discount: "Discount",
    currency: "Currency",
    amount: "Amount",
  };
  return labels[key] || String(key);
};

export const getDefaultItemsTable = (type) => ({
  columns: catalogForType(type).map(({ key, defaultLabel, align, visible }) => ({
    key,
    label: defaultLabel,
    visible,
    align,
  })),
});

/** Normalise GET /items-table-catalog response. */
export const normalizeItemsTableCatalog = (payload, type) => {
  const documentType = templateDocumentType(type);
  const fallback = catalogForType(documentType);

  const raw =
    (Array.isArray(payload?.columns) && payload.columns) ||
    (Array.isArray(payload?.data?.columns) && payload.data.columns) ||
    (Array.isArray(payload) && payload) ||
    [];

  if (!raw.length) return fallback;

  const merged = fallback.map((base) => {
    const fromApi = raw.find((col) => col?.key === base.key);
    if (!fromApi) return { ...base, defaultLabel: base.defaultLabel };

    return {
      key: base.key,
      defaultLabel: fromApi.defaultLabel || base.defaultLabel,
      align: ALIGNMENTS.includes(fromApi.align) ? fromApi.align : base.align,
      bindHint: fromApi.bindHint || base.bindHint,
      cssClass: fromApi.cssClass || base.cssClass,
    };
  });

  return merged;
};

export const getFallbackItemsTableCatalog = (type) =>
  catalogForType(type).map(({ key, defaultLabel, align, bindHint, cssClass }) => ({
    key,
    defaultLabel,
    align,
    bindHint,
    cssClass,
  }));

/**
 * Merge stored overrides with catalog defaults — mirrors backend resolveItemsTable.
 */
export const resolveItemsTable = (type, itemsTable) => {
  const defaults = catalogForType(type);
  if (!defaults.length) return { columns: [] };

  const overrides = Array.isArray(itemsTable?.columns) ? itemsTable.columns : [];
  const overrideMap = Object.fromEntries(
    overrides
      .filter((col) => col && isCatalogKey(type, col.key))
      .map((col) => [String(col.key).trim(), col])
  );

  const columns = defaults.map((def) => {
    const override = overrideMap[def.key] || {};
    return {
      key: def.key,
      label: String(override.label ?? def.defaultLabel).trim() || def.defaultLabel,
      visible: override.visible !== undefined ? Boolean(override.visible) : def.visible,
      align: ALIGNMENTS.includes(override.align) ? override.align : def.align,
      cssClass: def.cssClass,
      bindHint: def.bindHint,
      defaultLabel: def.defaultLabel,
    };
  });

  return { columns };
};

/** Keyed map for Handlebars dot access in client preview. */
export const columnsByKey = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.key] = {
      key: col.key,
      label: col.label,
      visible: col.visible,
      align: col.align,
      cssClass: col.cssClass,
    };
    return acc;
  }, {});

/** Full payload the server expects on save. */
export const itemsTablePayload = (type, itemsTable) => {
  const resolved = resolveItemsTable(type, itemsTable);
  return {
    columns: resolved.columns.map(({ key, label, visible, align }) => ({
      key,
      label,
      visible,
      align,
    })),
  };
};

export const itemsTableEqual = (a, b, type) => {
  const left = itemsTablePayload(type, a);
  const right = itemsTablePayload(type, b);
  return JSON.stringify(left) === JSON.stringify(right);
};

export const labelForColumnKey = (type, itemsTable, key) => {
  const resolved = resolveItemsTable(type, itemsTable);
  return resolved.columns.find((col) => col.key === key)?.label || key;
};

/** Handlebars source for the draggable Items Table block — matches server defaults. */
export const buildItemsTableBlockHtml = (type = "invoice") => {
  const documentType = templateDocumentType(type);
  const columns = catalogForType(documentType);

  const thead = columns
    .map(
      (col) =>
        `          <th class="${col.cssClass}">{{itemsTable.columns.${col.key}.label}}</th>`
    )
    .join("\n");

  const tbody = columns
    .map((col) => `            <td class="${col.cssClass}">${col.bindHint}</td>`)
    .join("\n");

  return `<table class="items">
      <thead>
        <tr>
${thead}
        </tr>
      </thead>
      <tbody>
        {{#each items}}
          <tr>
${tbody}
          </tr>
        {{/each}}
      </tbody>
    </table>`;
};
